import { h as __h } from "preact";
import { signal } from "@preact/signals";
import { Component, createRef, h } from "preact";
/** @import { ComponentChildren, RefObject } from "preact"; */

import { SearchParams } from "~/lib/search.js";
import html from "~/util/html.js";
import { pluralize } from "~/util/pluralize.js";
import Dropdown from "~/component/Dropdown.js";
/** @import { DropdownItem } from "~/component/Dropdown.js"; */
import Icon from "~/component/Icon.js";
import Pagination from "./Pagination.js";
/** @import { PaginationProps } from "./Pagination.js"; */

import button from "~/style/button.js";
import util from "~/style/util.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./Table.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./Table.module.css", import.meta.url);
const css = adopt(sheet);

/**
 * @template O
 * @template {string} T
 * @typedef {object} TableProps
 * @property {string} label
 * @property {Map<string, O>} [checked]
 * @property {(checked: Map<string, O>) => void} [onCheck]
 * @property {(obj: O) => string} [href]
 * @property {(obj: O, anchor: { current: HTMLButtonElement }) => DropdownItem[]} [onTripleDots]
 * @property {(obj: O) => ComponentChildren} [expand] Content of the row that opens below a row. A row
 *   for which this returns `null` or `undefined` cannot be expanded.
 * @property {ComponentChildren} empty
 * @property {ComponentChildren} [footer]
 * @property {TableHeader<T>[]} headers
 * @property {{ key: string; label: string; value: O; columns: Record<T, ComponentChildren> }[]} rows
 * @property {PaginationProps} [paginate]
 * @property {SortProps} [sort] Makes the headers with a `sort` key clickable to sort by them.
 * @property {false} [scroll]
 */

/**
 * @typedef {object} SortProps
 * @property {string} by
 * @property {"asc" | "desc"} order
 * @property {(by: string) => void} onSort Called with the sort key of the clicked header.
 */

/**
 * A table's sort, kept in the URL unless it's local. The URL leaves out the table's default sort,
 * and a sort key the endpoint doesn't accept falls back to it. Another key without an order sorts
 * ascending.
 *
 * @template {string} K
 */
class Sort {
  /** @type {K[]} */
  #keys;
  /** @type {K} */
  #by;
  /** @type {"asc" | "desc"} */
  #order;
  /** @type {{ value: { by: string | undefined; order: "asc" | "desc" | undefined } }} */
  #state;

  /**
   * @param {object} options
   * @param {K[]} options.keys The sort keys the endpoint accepts.
   * @param {K} options.by The key to sort by by default.
   * @param {"asc" | "desc"} [options.order] The order to sort in by default.
   * @param {boolean} [options.local] Keeps the sort out of the URL, for a table that shares its
   *   page with others.
   */
  constructor({
    keys,
    by,
    order = "asc",
    local = false
  }) {
    this.#keys = keys;
    this.#by = by;
    this.#order = order;
    this.#state = local ? signal({
      by: undefined,
      order: undefined
    }) : SearchParams.sort;
  }
  get by() {
    const by = this.#state.value.by;
    return this.#keys.find(key => key === by) ?? this.#by;
  }
  get order() {
    const {
      by,
      order
    } = this.#state.value;
    if (by === undefined || by === this.#by) return order ?? this.#order;
    if (by === this.by) return order ?? "asc";
    return this.#order;
  }
  query() {
    return {
      sort: this.by,
      order: this.order
    };
  }

  /** @returns {SortProps} */
  props() {
    return {
      by: this.by,
      order: this.order,
      onSort: key => this.#toggle(key)
    };
  }

  /**
   * Moves to the next of ascending by `key`, descending by `key` and the default, skipping one
   * that would leave the rows in the same order.
   *
   * @param {string} key
   */
  #toggle(key) {
    const {
      by,
      order
    } = this;

    /** @type {{ by: string; order: "asc" | "desc" }} */
    let next = {
      by: key,
      order: "asc"
    };
    if (by === key && order === "asc") next = {
      by: key,
      order: "desc"
    };else if (by === key && key !== this.#by) next = {
      by: this.#by,
      order: this.#order
    };
    this.#state.value = next.by === this.#by && next.order === this.#order ? {
      by: undefined,
      order: undefined
    } : next;
  }
}

/**
 * @template O
 * @template {string} [T=string]
 * @extends {Component<TableProps<O,T>>}
 */
export default class Table extends Component {
  static Sort = Sort;
  #id = crypto.randomUUID();

  /** @type {import("@preact/signals").Signal<Set<string>>} */
  #expanded = signal(new Set());

  /** @type {RefObject<HTMLDivElement>} */
  #scrollRef = createRef();
  /**
   * @param {TableProps<O,T>} prev
   * @override
   */
  componentDidUpdate(prev) {
    const {
      paginate,
      sort
    } = this.props;
    if (prev.paginate?.page === paginate?.page && prev.sort?.by === sort?.by && prev.sort?.order === sort?.order) return;
    if (this.props.scroll ?? true) this.#scrollRef.current?.scrollTo(0, 0);
  }
  render() {
    return __h("div", {
      "class": css.main
    }, __h("div", {
      "class": css.scroll,
      "data-scroll": this.props.scroll ?? true,
      ref: this.#scrollRef
    }, __h("table", {
      "class": css.table
    }, __h("caption", {
      "class": util.visuallyHidden
    }, "table of ", pluralize(2, this.props.label)), __h("thead", {}, __h("tr", {
      "class": css.row + " " + css.head
    }, this.#renderHead())), Boolean(this.props.rows.length) && __h("tbody", {}, this.props.rows.map(row => [__h("tr", {
      key: row.key,
      "class": css.row + " " + css.body,
      "data-expanded": this.#expanded.value.has(row.key)
    }, this.#renderRow(row)), this.#Expandable.detail(row)]))), !this.props.rows.length && __h("div", {
      "class": css.empty
    }, this.props.empty)), this.#renderPagination());
  }
  #renderHead() {
    return [this.props.href && __h("th", {
      "class": css.clickable
    }), this.#checkable && this.#Checkable.head(), this.props.headers.map((header, i) => this.#renderHeader(header, i)), this.props.expand && __h("th", {
      "class": css.expander
    }), this.props.onTripleDots && __h("th", {
      "class": css.tripleDots
    })];
  }

  /**
   * @param {TableHeader<T>} header
   * @param {number} i
   */
  #renderHeader(header, i) {
    const sort = this.props.sort;
    const key = header.sort;
    const order = sort && key && sort.by === key ? sort.order : undefined;
    return __h("th", {
      "class": this.#leadingClass(i),
      style: header.width ? {
        width: header.width
      } : undefined,
      "aria-sort": order && (order === "asc" ? "ascending" : "descending")
    }, sort && key ? __h("button", {
      type: "button",
      "class": css.sort,
      onClick: () => {
        sort.onSort(key);
        this.props.paginate?.onPageChange(0);
      }
    }, __h("span", {
      "class": css.label
    }, header.label), __h("span", {
      "class": css.arrows
    }, __h("span", {
      "class": css.up
    }, __h(Icon, {
      name: "sort-up",
      size: 14
    })), __h("span", {
      "class": css.down
    }, __h(Icon, {
      name: "sort-down",
      size: 14
    })))) : __h("div", {
      "class": util.truncate
    }, header.label));
  }
  get #checkable() {
    return Boolean(this.props.checked && this.props.onCheck);
  }

  /** @param {number} i */
  #leadingClass(i) {
    return i === 0 && !this.#checkable ? css.leading : undefined;
  }

  /** @param {typeof this.props.rows[number]} row */
  #renderRow(row) {
    return [this.props.href && this.#Clickable.body(row), this.#checkable && this.#Checkable.body(row), this.props.headers.map((header, i) => __h("td", {
      "class": this.#leadingClass(i)
    }, __h("div", {
      "class": css.cell + " " + util.truncate,
      "data-empty": header.empty
    }, row.columns[header.id]))), this.props.expand && this.#Expandable.body(row), this.props.onTripleDots && this.#TripleDots.body(row)];
  }
  #Checkable = {
    head: () => {
      const id = `${this.#id}-all`;
      const {
        checked,
        rows
      } = this.props;
      const len = rows.filter(row => checked?.has(row.key)).length;
      const state = len === 0 ? "none" : len === rows.length ? "all" : "some";
      return __h("th", {
        "class": css.checkbox
      }, __h("input", {
        type: "checkbox",
        id: id,
        checked: state !== "none",
        indeterminate: state === "some",
        onInput: () => this.#Checkable.checkAll(state)
      }), __h("label", {
        "for": id,
        "class": util.visuallyHidden
      }, "Select ", state === "all" ? "none" : "all"));
    },
    body: (/** @type {typeof this.props.rows[number]} row */row) => {
      const checked = this.props.checked;
      const id = `checkbox-${this.#id}-${row.key}`;
      return __h("td", {
        "class": css.checkbox,
        onMouseEnter: this.#Checkable.onDrag(row),
        onMouseLeave: this.#Checkable.onDrag(row)
      }, __h("input", {
        type: "checkbox",
        id: id,
        checked: checked?.has(row.key),
        onInput: () => this.#Checkable.toggleRow(row)
      }), __h("label", {
        "for": id,
        "class": util.visuallyHidden
      }, "select ", row.label));
    },
    onDrag: (/** @type {typeof this.props.rows[number]} */row) => (/** @type {MouseEvent} */ev) => {
      const {
        checked,
        onCheck
      } = this.props;
      if (!checked || !onCheck) return;
      if (!(ev.buttons & 1)) return;
      const newChecked = new Map(checked);
      if (ev.shiftKey) newChecked.delete(row.key);else newChecked.set(row.key, row.value);
      onCheck(newChecked);
    },
    /** @param {typeof this.props.rows[number]} row */
    toggleRow: row => {
      const {
        checked,
        onCheck
      } = this.props;
      if (!checked || !onCheck) return;
      const newChecked = new Map(checked);
      if (newChecked.has(row.key)) newChecked.delete(row.key);else newChecked.set(row.key, row.value);
      onCheck(newChecked);
    },
    checkAll: (/** @type {string} */state) => {
      const {
        checked,
        onCheck,
        rows
      } = this.props;
      if (!checked || !onCheck) return;
      const newChecked = new Map(checked);
      if (state === "all") for (const row of rows) newChecked.delete(row.key);else for (const row of rows) newChecked.set(row.key, row.value);
      onCheck(newChecked);
    }
  };
  #Expandable = {
    /** @param {typeof this.props.rows[number]} row */
    id: row => `detail-${this.#id}-${row.key}`,
    /** @param {typeof this.props.rows[number]} row */
    toggle: row => {
      const expanded = new Set(this.#expanded.value);
      if (expanded.has(row.key)) expanded.delete(row.key);else expanded.add(row.key);
      this.#expanded.value = expanded;
    },
    body: (/** @type {typeof this.props.rows[number]} row */row) => {
      if (this.props.expand?.(row.value) == null) return __h("td", {
        "class": css.expander
      });
      const open = this.#expanded.value.has(row.key);
      return __h("td", {
        "class": css.expander
      }, __h("button", {
        type: "button",
        "class": button.button,
        "data-level": "tertiary",
        "data-size": "small",
        "aria-expanded": open,
        "aria-controls": open ? this.#Expandable.id(row) : undefined,
        onClick: () => this.#Expandable.toggle(row)
      }, __h("span", {
        "class": css.chevron
      }, __h(Icon, {
        name: "down",
        size: 14
      })), __h("span", {
        "class": util.visuallyHidden
      }, open ? "Hide" : "Show", " details for ", row.label)));
    },
    detail: (/** @type {typeof this.props.rows[number]} row */row) => {
      const expand = this.props.expand;
      if (!expand || !this.#expanded.value.has(row.key)) return null;
      const content = expand(row.value);
      if (content == null) return null;
      const {
        href,
        checked,
        onCheck,
        onTripleDots,
        headers
      } = this.props;
      const lead = (href ? 1 : 0) + (checked && onCheck ? 1 : 0);
      const trail = 1 + (onTripleDots ? 1 : 0);
      return __h("tr", {
        key: row.key + "-detail",
        id: this.#Expandable.id(row),
        "class": css.row + " " + css.detail
      }, lead > 0 && __h("td", {
        colspan: lead
      }), __h("td", {
        colspan: headers.length
      }, content), __h("td", {
        colspan: trail
      }));
    }
  };
  #Clickable = {
    body: (/** @type {typeof this.props.rows[number]} row */row) => {
      const href = this.props.href;
      if (!href) return;
      return __h("td", {
        "class": css.clickable
      }, __h("a", {
        href: href(row.value)
      }, __h("span", {
        "class": util.visuallyHidden
      }, "open ", row.label)));
    }
  };
  #TripleDots = {
    body: (/** @type {typeof this.props.rows[number]} row */row) => {
      const tripleDots = this.props.onTripleDots;
      if (!tripleDots) return;
      const anchor = createRef();
      const items = tripleDots(row.value, anchor);
      if (!items.length) return __h("td", {
        "class": css.tripleDots
      });
      const id = `tripledots-${this.#id}-${row.key}`;
      return __h("td", {
        "class": css.tripleDots
      }, __h(Dropdown, {
        id: id,
        placement: "bottom-end",
        trigger: __h("button", {
          ref: anchor,
          type: "button",
          "class": button.button,
          "data-level": "tertiary",
          "data-size": "small",
          popovertarget: id
        }, __h(Icon, {
          name: "menu",
          size: 14
        })),
        items: items
      }));
    }
  };
  #renderPagination() {
    if (!this.props.paginate && !this.props.footer && !this.props.checked) return null;
    const len = this.props.checked?.size ?? 0;
    return __h("div", {
      "class": css.footer
    }, __h("span", {
      "class": css.left
    }, len && this.props.label ? `${len} ${pluralize(len, this.props.label)} selected` : this.props.footer), this.props.paginate && h(Pagination, this.props.paginate));
  }
}

/**
 * @template {string} [T=string]
 * @typedef {object} TableHeader
 * @property {T} id
 * @property {ComponentChildren} label
 * @property {string} [empty]
 * @property {string} [width]
 * @property {string} [sort] The key the endpoint sorts this column by.
 */